import type {
  GetServerSideProps,
  GetServerSidePropsContext,
  GetServerSidePropsResult
} from 'next';
import { ParsedUrlQuery } from 'querystring';
import { FC, useCallback, useEffect, useState } from 'react';
import postDrugSearch, {
  PostDrugResponse
} from 'src/api/requests/postDrugSearch';
import {
  Category,
  DrugItem,
  FirstCategoryLevel,
  SecondCategoryLevel,
  ThirdCategoryLevel,
  SortBy,
  SortDirection
} from 'src/api/types';
import { useRouter } from 'next/router';
import { extractPageNumber } from 'src/utils/links';
import Head from 'next/head';
import {
  getCategoryPageMetaData,
  getSchemaData,
  robotsTagContent
} from 'src/utils/seo';
import { useIntl } from 'react-intl';
import { getSearchRequestObject } from 'src/api/hooks/usePostSearchFilters';
import {
  CustomErrorProps,
  useHandleCustomErrorRedirect,
  triggerCustomStatusCodeRedirect
} from 'src/ssr/errorStatusCodeRedirect';
import { fetchCache } from 'src/api/caching/cacheUtils';
import debounce from 'lodash/debounce';
import getMultyLevelCategories from 'src/api/requests/getMultyLevelCategories';
import getSpecificCategory from 'src/api/requests/getSpecificCategory';
import CategoryContainerThirdLevel from 'src/page-components/category/CategoryContainerThirdLevel';
import logger from 'src/utils/logger';
import useUserLocation from 'src/hooks/useUserLocation';
import {
  GeneralServerProps,
  generalSSRProps,
  getCacheConfig,
  isQueryParamsValid,
  setSSRCacheControl
} from '../../ssr/generalSsrUtil';
import { Props } from '../../pages/[category]/[...nextParams]';

interface ThirdLevelCategoryProps extends GeneralServerProps, CustomErrorProps {
  categoriesList: Category[];
  firstLevelCategory: FirstCategoryLevel;
  secondLevelCategory: SecondCategoryLevel;
  categoriesInfo: ThirdCategoryLevel;
  ssrDrugSearchResponse: PostDrugResponse;
  popularDrugs: PostDrugResponse;
  cheapDrugs: PostDrugResponse;
  isFiltering: boolean;
  isSorting: boolean;
  pageNumber: number;
  isThirdLevelCategoryPage: boolean;
}

const ThirdLevelCategoryPage: FC<Props | ThirdLevelCategoryProps> = ({
  firstLevelCategory,
  secondLevelCategory,
  categoriesInfo,
  ssrDrugSearchResponse,
  popularDrugs,
  cheapDrugs,
  isFiltering,
  isSorting,
  pageNumber,
  hasError,
  statusCode
}) => {
  const intl = useIntl();
  const { query, locale } = useRouter();
  const userLocation = useUserLocation();

  const { filters, sortBy, sortDirection } = query;

  const { redirectToStatusCodePage } = useHandleCustomErrorRedirect({
    hasError,
    statusCode
  });

  const [drugList, setDrugList] = useState<DrugItem[]>(
    ssrDrugSearchResponse?.drugs || []
  );
  const [searchCount, setSearchCount] = useState<number>(0);
  const [totalDrugsCount, setTotalDrugsCount] = useState<number>(
    ssrDrugSearchResponse?.totalCount || 1
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedPages, setSelectedPages] = useState<number[]>([pageNumber]);

  useEffect(() => {
    setSelectedPages([pageNumber]);
  }, [pageNumber]);

  const getSearchObject = useCallback(
    (loadMorePageNumber?: number) => {
      const searchObject = getSearchRequestObject(
        loadMorePageNumber || pageNumber,
        null,
        filters,
        sortBy,
        sortDirection
      );
      return {
        ...searchObject,
        filters: [
          ...searchObject.filters,
          {
            id: 'categoryId',
            valueIds: [categoriesInfo?.translitName ?? '']
          }
        ]
      };
    },
    [pageNumber, categoriesInfo?.translitName, filters, sortBy, sortDirection]
  );

  const debouncedDrugsUpdate = useCallback(
    debounce(() => {
      const drugSearchRequest = postDrugSearch(getSearchObject());
      drugSearchRequest
        .then((drugSearchResult) => {
          setDrugList(drugSearchResult?.drugs || []);
          setTotalDrugsCount(drugSearchResult?.totalCount || 1);
        })
        .catch(() => {
          // Clean up drug list because error happend
          setDrugList([]);
          setTotalDrugsCount(1);
        })
        .finally(() => {
          setIsLoading(false);
          setSearchCount((count) => count + 1);
        });
    }, 250),
    [
      pageNumber,
      categoriesInfo?.translitName,
      userLocation?.name,
      locale,
      filters,
      sortBy,
      sortDirection
    ]
  );

  useEffect(() => {
    setIsLoading(true);
    debouncedDrugsUpdate();
  }, [debouncedDrugsUpdate]);

  const handleLoadMoreSubmit = (loadMorePageNumber: number) => {
    setIsLoading(true);
    const drugSearchRequest = postDrugSearch(
      getSearchObject(loadMorePageNumber)
    );
    drugSearchRequest
      .then((drugSearchResult) => {
        // Add newly loaded drugs to the existing list
        const extendedDrugs = [...drugList, ...(drugSearchResult?.drugs || [])];
        setDrugList(extendedDrugs);
        setSelectedPages([...selectedPages, loadMorePageNumber]);
      })
      .catch((error) => {
        logger.log(error);
      })
      .finally(() => {
        setIsLoading(false);
        setSearchCount((count) => count + 1);
      });
  };

  if (hasError) {
    return redirectToStatusCodePage();
  }

  const seoData = getCategoryPageMetaData(
    categoriesInfo?.name || '',
    pageNumber,
    locale
  );

  return (
    <>
      <Head>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        {/* Prevent indexing if filtering or sorting provided */}

        <meta name="robots" content={robotsTagContent} key="robots" />

        {!isFiltering ||
          (!isSorting && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify(
                  getSchemaData(
                    categoriesInfo?.name,
                    popularDrugs,
                    cheapDrugs,
                    intl
                  )
                )
              }}
            />
          ))}
      </Head>
      <CategoryContainerThirdLevel
        drugList={drugList}
        selectedFirstLevelCategory={firstLevelCategory}
        selectedSecondLevelCategory={secondLevelCategory}
        selectedThirdLevelCategory={categoriesInfo}
        totalDrugsCount={totalDrugsCount}
        selectedPages={selectedPages}
        isLoading={isLoading}
        handleLoadMoreSubmit={handleLoadMoreSubmit}
        isSEOTextHidden={isFiltering || isSorting || pageNumber > 1}
        finalEntityName={categoriesInfo?.name}
        popularDrugs={popularDrugs}
        cheapDrugs={popularDrugs}
        searchObject={getSearchObject()}
        searchCount={searchCount}
      />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext<ParsedUrlQuery>
) => {
  setSSRCacheControl(context);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return fetchCache<GetServerSidePropsResult<{ [key: string]: any }>>(
    getCacheConfig(context),
    async () => {
      const {
        res,
        req: { cookies },
        query
      } = context;
      const generalProps = await generalSSRProps(context);

      if (!isQueryParamsValid(query)) {
        // Redirect to 410 because the URL contains invalid query params
        return triggerCustomStatusCodeRedirect(res, 410);
      }

      const { category, filters, sortBy, sortDirection, nextParams } = query;
      const [secondCategoryName, thirdCategoryName, page] =
        nextParams as string[];
      const pageNumber = extractPageNumber(page);

      const multyLevelCategoriesList = await getMultyLevelCategories({
        cookies,
        ip: generalProps.ip,
        locale: context?.locale,
        useCache: true
      });

      const firstLevelCategory = multyLevelCategoriesList?.find(
        (subC) => subC?.translitName === (category as string)
      );

      const secondLevelCategory = await getSpecificCategory(
        {
          level: '1',
          translitName: secondCategoryName as string
        },
        { cookies, ip: generalProps.ip, locale: context?.locale }
      );

      const categoriesInfo = await getSpecificCategory(
        {
          level: '2',
          translitName: thirdCategoryName as string
        },
        { cookies, ip: generalProps.ip, locale: context?.locale }
      );

      const searchObject = getSearchRequestObject(
        pageNumber,
        null,
        filters,
        sortBy,
        sortDirection
      );

      const drugSearchResponse = await postDrugSearch(
        {
          ...searchObject,
          filters: [
            {
              id: 'categoryId',
              valueIds: [categoriesInfo?.translitName ?? '']
            },
            ...searchObject.filters
          ]
        },
        {
          cookies,
          ip: generalProps.ip,
          locale: context?.locale,
          useCache: true
        }
      );

      const isFiltering = !!filters;
      const isSorting = !!sortBy || !!sortDirection;

      const getRequestBody = (sortByParam: SortBy) => {
        const body = getSearchRequestObject(
          1,
          null,
          filters,
          sortByParam,
          SortDirection.Asc,
          3
        );
        return {
          ...body,
          filters: [
            {
              id: 'categoryId',
              valueIds: [categoriesInfo?.translitName ?? '']
            },
            ...body.filters
          ]
        };
      };
      const [popularDrugs, cheapDrugs] = await Promise.all([
        postDrugSearch(getRequestBody(SortBy.popularity)),
        postDrugSearch(getRequestBody(SortBy.price))
      ]);

      if (!categoriesInfo) {
        // Redirect to 410 if category not found
        return triggerCustomStatusCodeRedirect(res, 410);
      }

      return {
        props: {
          ...generalProps,
          firstLevelCategory,
          secondLevelCategory,
          categoriesInfo,
          ssrDrugSearchResponse: drugSearchResponse,
          popularDrugs,
          cheapDrugs,
          isFiltering,
          isSorting,
          pageNumber,
          isThirdLevelCategoryPage: true
        }
      };
    }
  );
};

export default ThirdLevelCategoryPage;
